// src/components/styles/StallCards.styles.ts
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { theme } from './home.styles';

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const flip = keyframes`
  0% { transform: rotateY(0deg); }
  50% { transform: rotateY(180deg); }
  100% { transform: rotateY(360deg); }
`;

// ──────────────────────────────────────────────────────────────
// 1. NEW – Fixed‑size wrapper for STANDARD stall cards only
// ──────────────────────────────────────────────────────────────
export const StandardCardWrapper = styled.div`
  width: 340px;          // exact same width as RealEstate cards
  height: 450px;         // exact same height (media + content)
  display: flex;
  flex-direction: column;
`;

// ──────────────────────────────────────────────────────────────
// 2. Apply the wrapper ONLY to the regular CardContainer
// ──────────────────────────────────────────────────────────────
export const CardContainer = styled(StandardCardWrapper)`
  background: ${theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.4s ease-out;
  border: 1px solid ${theme.colors.borderLight};
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
`;

// ──────────────────────────────────────────────────────────────
// 3. PremiumCardContainer stays untouched (no wrapper)
// ──────────────────────────────────────────────────────────────
export const PremiumCardContainer = styled.div<{ flip?: boolean }>`
  border: 2px solid ${theme.colors.primary};
  box-shadow: 0 4px 20px rgba(255, 153, 0, 0.2);
  background: linear-gradient(135deg, ${theme.colors.white} 0%, #fffdf9 100%);
  overflow: visible;
  border-radius: 12px;               // keep the original radius
  position: relative;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.4s ease-out;

  ${props => props.flip && css`
    animation: ${flip} 1s ease-in-out, ${fadeIn} 0.4s ease-out;
  `}

  @media (min-width: 600px) {
    grid-column: span 2;            // premium still spans 2 columns
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(255, 153, 0, 0.25);
  }
`;

// export const PremiumCardContainer = styled(CardWrapper)<{ flip?: boolean }>`
//   border: 2px solid ${theme.colors.primary};
//   box-shadow: 0 4px 20px rgba(255, 153, 0, 0.2);
//   background: linear-gradient(135deg, ${theme.colors.white} 0%, #fffdf9 100%);
//   overflow: visible;

//   ${props => props.flip && css`
//     animation: ${flip} 1s ease-in-out, ${fadeIn} 0.4s ease-out;
//   `}

//   @media (min-width: 600px) { grid-column: span 2; }
// `;

export const PremiumLabel = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, ${theme.colors.primary}, #ff8c00);
  color: ${theme.colors.white};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(255, 153, 0, 0.3);
  z-index: 10;
  font-family: ${theme.typography.fontFamily};
  letter-spacing: 0.5px;
`;

export const CombinedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 16px 16px;
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

export const MetaText = styled.span`
  font-weight: 500;
`;

export const QuickStats = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.borderLight};
  border-bottom: 1px solid ${theme.colors.borderLight};
  margin: 4px 0;
  background-color: ${theme.colors.background};
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const StatValue = styled.span`
  font-size: 1.1rem;
  font-weight: 900;
  color: ${theme.colors.primaryDark};
`;

export const StatLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ViewDetailsButton = styled.button`
  background: ${theme.colors.primary}; 
  color: ${theme.colors.white};
  border: none; 
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 800;
  transition: all 0.2s ease;
  display: flex; 
  width: 100%; 
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${theme.typography.fontFamily};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(255, 153, 0, 0.25);

  &:hover {
    background: ${theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 6px 15px rgba(255, 153, 0, 0.35);
  }

  // NEW: Specific styles for Premium View Details Button
  &.premium-view-details-button {
    background: #007bff; /* Blue color */
    color: white;
    box-shadow: none;
    font-size: 0.85rem; /* Smaller font */
    padding: 10px 16px; /* Adjusted padding */
    border-radius: 6px; 
    text-transform: none; 
    letter-spacing: normal; 
    width: fit-content; 

    &:hover {
        background: #0056b3; /* Darker blue on hover */
        transform: none; 
        box-shadow: none; 
    }
  }
`;

export const SocialIconsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${theme.colors.borderLight};

  // NEW: Specific styles for Premium Social Icons
  &.premium-social-icons {
    margin-top: 15px; 
    padding-top: 0; 
    border-top: none; 
    justify-content: flex-start; 
    gap: 15px; 
  }
`;

export const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${theme.colors.background};
  border-radius: 8px;
  color: ${theme.colors.text};
  transition: all 0.2s ease;
  border: 1px solid ${theme.colors.borderLight};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:hover:nth-child(1) { background: #25D366; color: white; border-color: #25D366; }
  &:hover:nth-child(2) { background: #1877F2; color: white; border-color: #1877F2; }
  &:hover:nth-child(3) { background: #1DA1F2; color: white; border-color: #1DA1F2; }
  &:hover:nth-child(4) { background: #000000; color: white; border-color: #000000; }
  &:hover:nth-child(5) { background: #FF0000; color: white; border-color: #FF0000; }
  &:hover:nth-child(6) { background: #E4405F; color: white; border-color: #E4405F; }

  // NEW: Specific styles for Premium Social Icons
  .premium-social-icons & {
    background: transparent; 
    border: none; 
    color: ${theme.colors.textMuted}; 
    width: auto; 
    height: auto; 
    
    &:hover {
      transform: none; 
      box-shadow: none; 
      color: ${theme.colors.primary}; 
    }

    svg {
        font-size: 1.5rem; 
    }
  }
`;

export const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4/3; 
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); 
`;

export const CardHeader = styled.div`
  padding: 0 16px;
`;

export const MetaIcon = styled(FontAwesomeIcon)`
  font-size: 0.9rem;
  width: 14px;
  color: ${theme.colors.textMuted};

  // NEW: Styles for Premium Meta Icons
  .premium-business-meta & {
    font-size: 0.8rem; 
    color: ${theme.colors.textMuted}; 
  }
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${theme.colors.text};
  font-weight: 500;

  // NEW: Styles for Premium Meta Items
  .premium-business-meta & {
    flex-shrink: 0; 
    font-size: 0.8rem; 
    color: ${theme.colors.textMuted}; 

    &:first-child {
        flex-shrink: 1; 
        min-width: 0; 
        overflow: hidden; 
        text-overflow: ellipsis; 
        max-width: 100%; 
        white-space: nowrap; 
        font-weight: 700; 
        color: ${theme.colors.black}; 
        
        ${MetaIcon} { 
            color: ${theme.colors.textMuted}; 
        }
    }

    // Adjust specific meta items
    &:nth-child(2), &:nth-child(3), &:nth-child(4) { 
        color: ${theme.colors.text}; 
    }

    &:nth-child(3) { 
      max-width: 150px; 
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &:nth-child(4) { 
      max-width: 120px; 
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const CardBody = styled.div`
  padding: 12px 0 0; 
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Badge = styled.span<{ type: 'category' | 'location' | 'feature' }>`
  padding: 0; 
  border-radius: 0;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.1px;
  box-shadow: none; 
  
  /* Use muted text color */
  background: ${props => {
    switch (props.type) {
      case 'category': return `none`;
      default: return theme.colors.background;
    }
  }};
  color: ${theme.colors.textMuted}; 
`;

export const CardFooter = styled.div`
  padding: 16px; 
  border-top: none; 
  background: ${theme.colors.white}; 
`;

export const MediaItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; 
  background: white;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

export const PremiumMediaContainer = styled(MediaContainer)`
  aspect-ratio: 16/9; 
  background: transparent; // Remove gradient background
  border-radius: 0; // Remove any border radius
  overflow: hidden; // Keep media contained
  margin: -2px -2px 0 -2px; // Compensate for border and allow full width
  width: calc(100% + 4px); // Extend beyond container to fill borders
`;

export const BusinessTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0;
  line-height: 1.3;
  padding: 0 16px;
 
  // UPDATED: Styles for Premium Card Title - more compact
  .premium-card-title & {
    font-size: 1.1rem; 
    font-weight: 700; 
    padding: 12px 16px 8px; // Adjusted padding
    border-bottom: none;
  }
`;

// UPDATED: Remove border and adjust padding for premium business meta
export const BusinessMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 16px; 

  // UPDATED: Styles for Premium Business Meta - more compact
  &.premium-business-meta {
    gap: 12px; 
    margin: 0; 
    padding: 0 16px 12px; // Reduced padding
    border-bottom: none; // Remove border
    flex-wrap: nowrap; 
    overflow-x: auto; 
    white-space: nowrap; 
    -ms-overflow-style: none; 
    scrollbar-width: none; 

    &::-webkit-scrollbar { 
      display: none;
    }
  }
`;

// UPDATED: ActionBar for premium - make buttons more compact
export const ActionBar = styled.div`
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr)); 
  gap: 8px;
  margin-bottom: 16px;

  // UPDATED: Premium ActionBar styles
  &.premium-action-bar {
    display: flex;
    justify-content: space-between;
    gap: 0;
    margin-bottom: 12px; // Reduced margin
    padding-bottom: 0; // Remove padding
    border-bottom: none; // Remove separator line
    
    // Remove all border radius overrides - let buttons have their own radius
    a {
      border-radius: 6px !important; // Consistent small radius
      flex: 1; // Equal width
      margin: 0 2px; // Small gap between buttons
      
      &:first-child {
        margin-left: 0;
      }
      
      &:last-child {
        margin-right: 0;
      }
    }
  }
`;

// UPDATED: Premium contact buttons - more compact
export const FullWidthContactButton = styled.a<{ buttonType: 'phone' | 'email' | 'whatsapp' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.85rem; 
  font-weight: 700;
  transition: all 0.2s ease;
  flex-grow: 1;

  ${props => {
    switch (props.buttonType) {
      case 'phone':
        return css`
          background: #25D366;
          color: ${theme.colors.white};
          
          &:hover { background: #1ea952; }

          &.premium { 
            background: #ffa500;
            color: white;
            box-shadow: none; 
            flex-grow: 1;
            padding: 8px 6px; // More compact padding
            font-size: 0.75rem; 
            border-radius: 6px; // Consistent radius
            
            &:hover {
              background: #cc8400;
            }
          }
        `;
      case 'email':
        return css`
          background: ${theme.colors.white};
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};
          
          &:hover { background: ${theme.colors.background}; }

          svg { display: none; }

          &.premium {
            background: #ffa500;
            color: white;
            border: none;
            box-shadow: none;
            flex-grow: 1;
            padding: 8px 6px; // More compact padding
            font-size: 0.75rem;
            border-radius: 6px; // Consistent radius

            svg {
              display: inline-block; 
            }

            &:hover {
              background: #cc8400;
            }
          }
        `;
      case 'whatsapp':
        return css`
          background: #25D366;
          color: ${theme.colors.white};
          box-shadow: 0 2px 4px rgba(37, 211, 102, 0.5);

          &:hover { background: #128C7E; }

          svg { display: none; }

          &.premium {
            background: #ffa500;
            color: white;
            box-shadow: none;
            flex-grow: 1;
            padding: 8px 6px; // More compact padding
            font-size: 0.75rem;
            border-radius: 6px; // Consistent radius

            svg {
              display: inline-block; 
            }

            &:hover {
              background: #25D366;
            }
          }
        `;
    }
  }}
`;

// UPDATED: Remove badge container borders and adjust spacing
export const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px; 
  border-bottom: none; // Remove border

  // For premium cards
  .premium-card & {
    padding: 4px 16px 12px; // Adjusted padding
    justify-content: flex-start; // Align left
  }
`;
